import { RouteData } from '@cya-routes/shared'
import { 
  Route, 
  Eye,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight,
  ChevronDown,
  ChevronRight
} from 'lucide-react'
import { SurfaceBreakdown } from '../editor/SurfaceBreakdown'
import { calculateRouteGradient } from '../../services/elevationGradientService'

interface RouteNavigatorProps {
  routes: RouteData[]
  masterRoute: RouteData | null | undefined
  selectedRouteId: string
  showDetailedStats: Record<string, boolean>
  showElevation: boolean
  hasCurrentRoute: boolean
  onRouteSelect: (routeId: string) => void
  onShowDetailedStatsToggle: (routeId: string) => void
  onShowElevation: () => void
}

export function RouteNavigator({
  routes,
  masterRoute,
  selectedRouteId,
  showDetailedStats,
  showElevation,
  hasCurrentRoute,
  onRouteSelect,
  onShowDetailedStatsToggle,
  onShowElevation
}: RouteNavigatorProps) {
  const hasMasterRoute = routes.length > 1 && masterRoute
  
  return (
    <div className="p-1 sm:p-2 space-y-2">
      {/* Master Route (if available) */}
      {hasMasterRoute && (
        <>
          <div
            onClick={() => onRouteSelect('master-route')}
            className={`p-1 sm:p-2 rounded-lg border cursor-pointer transition-all ${
              selectedRouteId === 'master-route'
                ? 'border-emerald-500 bg-emerald-900/20'
                : 'border-gray-600 bg-gray-700/50 hover:bg-gray-700'
            }`}
          >
            <div className="flex items-center justify-between mb-0 sm:mb-2">
              {/* Mobile: M badge and distance on same line */}
              <div className="sm:hidden flex items-center space-x-2">
                <span className="w-[18px] h-[18px] rounded-md flex items-center justify-center text-[10px] font-bold text-emerald-300 bg-emerald-700">
                  M
                </span>
                <span className="text-[9px] text-gray-400">{Math.round(masterRoute.stats.distance)}km</span>
              </div>
              {/* Desktop: title */}
              <h4 className="text-sm font-medium text-gray-100 hidden md:block">Complete Route</h4>
              {/* Tablet/Desktop: MASTER pill */}
              <span className="hidden sm:inline-block px-2 py-1 rounded-md text-xs font-medium text-emerald-300 bg-emerald-700">
                MASTER
              </span>
            </div>
            <div className="hidden sm:flex items-center gap-3 text-xs">
              {/* Tablet/Desktop: full distance with icon */}
              <div className="flex items-center gap-1">
                <Route className="w-3 h-3 text-emerald-500" />
                <span className="text-gray-100">{masterRoute.stats.distance.toFixed(1)} km</span>
              </div>
              <div className="hidden md:flex items-center gap-1">
                <ArrowUpRight className="w-3 h-3 text-emerald-500" />
                <span className="text-gray-100">{masterRoute.elevation.up.toLocaleString()}m</span>
              </div>
              <div className="hidden md:flex items-center gap-1">
                <ArrowDownRight className="w-3 h-3 text-emerald-500" />
                <span className="text-gray-100">{masterRoute.elevation.down.toLocaleString()}m</span>
              </div>
            </div>
            <div className="hidden md:flex mt-2 items-center justify-between text-xs">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-emerald-500" />
                  <span className="text-gray-100">{masterRoute.stats.surfaceBreakdown ? 
                    `${Math.round(masterRoute.stats.surfaceBreakdown.unpaved.percentage)}%` :
                    `${Math.round(masterRoute.stats.unpaved || 0)}%`
                  } unpaved</span>
                </div>
                <div className="flex items-center gap-1">
                  <div 
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: calculateRouteGradient(masterRoute.elevation.up, masterRoute.stats.distance).color }}
                  />
                  <span className="text-gray-100">{calculateRouteGradient(masterRoute.elevation.up, masterRoute.stats.distance).label}</span>
                </div>
              </div>
              {/* Toggle for detailed breakdown */}
              {masterRoute.stats.surfaceBreakdown && masterRoute.stats.highwayBreakdown && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onShowDetailedStatsToggle('master-route')
                  }}
                  className="p-1 text-gray-500 hover:text-gray-300 transition-colors"
                >
                  {showDetailedStats['master-route'] ? (
                    <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronRight className="w-3 h-3" />
                  )}
                </button>
              )}
            </div>
            {/* Detailed Surface Breakdown */}
            {showDetailedStats['master-route'] && masterRoute.stats.surfaceBreakdown && masterRoute.stats.highwayBreakdown && (
              <div className="mt-3 pt-3 border-t border-gray-600">
                <SurfaceBreakdown route={masterRoute} />
              </div>
            )}
          </div>
          
          {/* Divider */}
          <div className="hidden sm:flex items-center gap-3 py-2">
            <div className="flex-1 h-px bg-gray-600"></div>
            <span className="text-xs text-gray-400 uppercase tracking-wide">Segments</span>
            <div className="flex-1 h-px bg-gray-600"></div>
          </div>
        </>
      )}
      
      {/* Individual Route Segments */}
      {routes.map((route, index) => (
        <div
          key={route.id}
          onClick={() => onRouteSelect(route.id)}
          className={`p-1 sm:p-2 rounded-lg border cursor-pointer transition-all ${
            selectedRouteId === route.id
              ? 'border-emerald-500 bg-emerald-900/20'
              : 'border-gray-600 bg-gray-700/50 hover:bg-gray-700'
          }`}
        >
          <div className="flex items-center space-x-2 mb-0 sm:mb-2">
            {/* Color indicator with index number */}
            <div
              className="w-[18px] h-[18px] rounded-full border border-gray-700 shadow-sm flex-shrink-0 flex items-center justify-center text-[10px] font-bold text-white"
              style={{ backgroundColor: route.color }}
            >
              {index + 1}
            </div>
            {/* Mobile: distance on same line */}
            <span className="sm:hidden text-[9px] text-gray-400">{Math.round(route.stats.distance)}km</span>
            {/* Tablet/Desktop: route name */}
            <h4 className="text-sm font-medium text-gray-100 flex-1 hidden sm:block">{route.name}</h4>
          </div>
          <div className="hidden sm:flex items-center gap-3 text-xs">
            {/* Tablet/Desktop: full distance with icon */}
            <div className="flex items-center gap-1">
              <Route className="w-3 h-3 text-emerald-500" />
              <span className="text-gray-100">{route.stats.distance.toFixed(1)} km</span>
            </div>
            <div className="hidden md:flex items-center gap-1">
              <ArrowUpRight className="w-3 h-3 text-emerald-500" />
              <span className="text-gray-100">{route.elevation?.up?.toLocaleString() || 0}m</span>
            </div>
            <div className="hidden md:flex items-center gap-1">
              <ArrowDownRight className="w-3 h-3 text-emerald-500" />
              <span className="text-gray-100">{route.elevation?.down?.toLocaleString() || 0}m</span>
            </div>
          </div>
          <div className="hidden md:flex mt-2 items-center justify-between text-xs">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-emerald-500" />
                <span className="text-gray-100">{route.stats.surfaceBreakdown ? 
                  `${Math.round(route.stats.surfaceBreakdown.unpaved.percentage)}%` :
                  `${Math.round(route.stats.unpaved || 0)}%`
                } unpaved</span>
              </div>
              <div className="flex items-center gap-1">
                <div 
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: calculateRouteGradient(route.elevation?.up || 0, route.stats.distance).color }}
                />
                <span className="text-gray-100">{calculateRouteGradient(route.elevation?.up || 0, route.stats.distance).label}</span>
              </div>
            </div>
            {/* Toggle for detailed breakdown */}
            {route.stats.surfaceBreakdown && route.stats.highwayBreakdown && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onShowDetailedStatsToggle(route.id)
                }}
                className="p-1 text-gray-500 hover:text-gray-300 transition-colors"
              >
                {showDetailedStats[route.id] ? (
                  <ChevronDown className="w-3 h-3" />
                ) : (
                  <ChevronRight className="w-3 h-3" />
                )}
              </button>
            )}
          </div>
          {/* Detailed Surface Breakdown */}
          {showDetailedStats[route.id] && route.stats.surfaceBreakdown && route.stats.highwayBreakdown && (
            <div className="mt-3 pt-3 border-t border-gray-600">
              <SurfaceBreakdown route={route} />
            </div>
          )}
        </div>
      ))}
      
      {!showElevation && selectedRouteId && hasCurrentRoute && (
        <div className="pt-4 border-t border-gray-600">
          <button
            onClick={onShowElevation}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
          >
            <Eye className="w-4 h-4" />
            <span>Show Elevation Profile</span>
          </button>
        </div>
      )}
    </div>
  )
}